import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/Avatar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/Card";

const testimonialsData = [
  {
    id: "user1",
    name: "Priyvrat",
    review:
      "Best app ....makes the preparation more engaging and learning simpler through quality questions! (100/100)",
    imageUrl: `${process.env.PUBLIC_URL}/priyavrat.webp`,
    rating: 5,
    dataAiHint: "student smiling",
  },
  {
    id: "user2",
    name: "Athuljith t k",
    review:
      "This app is a hidden gem for UPSC aspirants. I joined via their Telegram group and got hooked. The smart PYQs and the detailed performance analytics help me stay focused and track improvement.❤️👍",
    imageUrl: `${process.env.PUBLIC_URL}/athuljit.webp`,
    rating: 5,
    dataAiHint: "person happy",
  },
  {
    id: "user3",
    name: "Spidey",
    review:
      "A refreshing break from boring MCQ layouts - swipe format is a total win!",
    imageUrl:
      "https://play-lh.googleusercontent.com/a-/ALV-UjUJ0byBlQQrWg8ykrgrvilBKBadvtjCb7V1eCk3m3CBUPrCQ9CSRg=s32-rw",
    rating: 5,
    dataAiHint: "student study",
  },
  {
    id: "user4",
    name: "Swalih",
    review:
      "I usually skip these reviews, but PrepAiro deserves praise. It's really effective.",
    imageUrl: `${process.env.PUBLIC_URL}/swalih.webp`,
    rating: 5,
    dataAiHint: "friends study",
  },
];

const placeholderTestimonialsData = [
  {
    id: "placeholderUserA",
    name: "Alwin Jose",
    review:
      "Been using this for a week - great for Prelims. Also love how it's totally ad-free! I wish they would add something for mains as well. Great user interface as well.",
    imageUrl: `${process.env.PUBLIC_URL}/jose.webp`,
    rating: 5,
    dataAiHint: "student learning",
  },
  {
    id: "placeholderUserB",
    name: "NihalGrace",
    review: "One look at the dashboard, and I know what to revise next.",
    imageUrl:
      "https://play-lh.googleusercontent.com/a/ACg8ocLKNQiO7FyfWTFMf5-tnKHO8EeR7yVZtFO_mguo1NkeQLm_H6w=s32-rw-mo",
    rating: 5,
    dataAiHint: "happy student",
  },
  {
    id: "placeholderUserC",
    name: "PRINCE JAISON",
    review: "Makes self-study social - didn't know that was possible.❤️❤️",
    imageUrl: `${process.env.PUBLIC_URL}/prince.webp`,
    rating: 5,
    dataAiHint: "focused learner",
  },
  {
    id: "placeholderUserD",
    name: "Sharon Menezes",
    review: "Great tracking! Mistakes are saved so I can focus on improving.",
    imageUrl: `${process.env.PUBLIC_URL}/sharon.webp`,
    rating: 5,
    dataAiHint: "student using app",
  },
];

const scrollTransition = {
  ease: "linear",
  duration: 40,
  repeat: Infinity,
  repeatType: "loop",
};

const TestimonialCard = ({ testimonial, index, isFirstRow }) => (
  <motion.div
    key={`${testimonial.id}-${isFirstRow ? "row1" : "row2"}-${index}`}
    className="flex-shrink-0 w-[70vw] sm:w-[250px] md:w-[300px]"
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{
      duration: 0.5,
      delay:
        (index %
          (isFirstRow
            ? testimonialsData.length
            : placeholderTestimonialsData.length)) *
        0.1,
    }}
  >
    <Card className="h-full flex flex-col bg-[#EFF1F3] border border-[#b0b6bb] rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="h-10 w-10">
          <AvatarImage
            src={testimonial.imageUrl}
            alt={testimonial.name}
            data-ai-hint={testimonial.dataAiHint}
          />
          <AvatarFallback>
            {testimonial.name.substring(0, 1)}
            {testimonial.name.split(" ")[1]?.substring(0, 1) || ""}
          </AvatarFallback>
        </Avatar>
        <CardTitle className="text-sm font-semibold">
          {testimonial.name}
        </CardTitle>
      </div>
      <CardContent className="flex-grow text-sm sm:text-base text-foreground/90 leading-relaxed">
        <p className="mb-2">&ldquo;{testimonial.review}&rdquo;</p>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < testimonial.rating
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-muted-foreground/50"
              }`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default function TestimonialCarousel() {
  const row1 = [...testimonialsData, ...testimonialsData];
  const row2 = [...placeholderTestimonialsData, ...placeholderTestimonialsData];

  return (
    <section
      id="testimonials"
      className="w-full py-16 md:py-24 bg-secondary/50 overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          Hear What Our <span className="text-primary">UPSC Aspirants Say</span>
        </motion.h2>

        <div className="space-y-8">
          {[row1, row2].map((row, idx) => (
            <div
              key={idx}
              className="relative w-full overflow-hidden max-w-full mx-auto"
            >
              <motion.div
                className="flex gap-3 sm:gap-4 md:gap-6"
                animate={{ x: idx === 0 ? ["0%", "-50%"] : ["-50%", "0%"] }}
                transition={scrollTransition}
              >
                {row.map((t, i) => (
                  <TestimonialCard
                    testimonial={t}
                    index={i}
                    isFirstRow={idx === 0}
                    key={t.id + i}
                  />
                ))}
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
